// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import ChevronUp from "@store/Library/Components/Toolkit/Icons/ChevronUp";
import { buttonResetStyles } from "@store/Themes/common/mixins/button";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import createStyleguideSection from "@toolkit/Framework/Store/Toolkit/Components/Styleguide/Handlers/CreateStyleguideSection";
import removeStyleguideSection from "@toolkit/Framework/Store/Toolkit/Components/Styleguide/Handlers/RemoveStyleguideSection";
import setIsStyleguideSectionOpen from "@toolkit/Framework/Store/Toolkit/Components/Styleguide/Handlers/SetIsStyleguideSectionOpen";
import { useIsStyleguideSectionOpen } from "@toolkit/Framework/Store/Toolkit/Components/Styleguide/StyleguideHooks";
import { focusStyles } from "@toolkit/Themes/common/mixins/common";
import React, { FC, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

export interface StyleguideSectionProps extends ExpandStyles<StyleguideSectionStyles> {
    title: string;
    variant?: TypographyPresentationProps["variant"];
    isSubSection?: boolean;
    sectionId?: string;
}

export interface StyleguideSectionStyles {
    container?: InjectableCss;
    clickable?: ClickablePresentationProps;
    title?: TypographyPresentationProps;
    toggle?: ButtonPresentationProps;
    wrapper?: InjectableCss;
}

const borderFirstLevelSection = css`
    ${({ theme }) => `3px solid ${resolveColor("common.border", theme)}`}
`;

const borderSecondLevelSection = css`
    ${({ theme }) => `1px solid ${resolveColor("common.border", theme)}`}
`;

const styleguideSectionStyles: StyleguideSectionStyles = {
    container: {
        css: css`
            &:first-of-type {
                border-top: ${borderFirstLevelSection};
            }
        `,
    },
    title: {
        variant: "h3",
        forwardAs: "h2",
        css: css`
            margin-bottom: 0;
            border-bottom: ${borderFirstLevelSection};
        `,
    },
    toggle: {
        css: css`
            ${buttonResetStyles()}
            ${focusStyles()}
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding: ${pxToRem(20, 0)};
            color: inherit;

            &:hover,
            &:active,
            &[aria-expanded="true"] {
                color: ${getColor("primary")};
            }
        `,
    },
    wrapper: {
        css: css`
            padding: ${pxToRem(40, 0)};
            border-bottom: ${borderFirstLevelSection};
        `,
    },
};

const StyleguideSection: FC<StyleguideSectionProps> = ({
    children,
    isSubSection,
    sectionId,
    title,
    variant,
    extendedStyles,
    overridedStyles,
}) => {
    const dispatch = useAppDispatch();
    const baseStyles = useExpandProps(styleguideSectionStyles, extendedStyles, overridedStyles);
    const isOpen = useIsStyleguideSectionOpen(sectionId || "");
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const isSectionOpen = isOpen || isVisible;

    useEffect(() => {
        if (sectionId) {
            dispatch(createStyleguideSection({ id: sectionId, isOpen: !!isSubSection }));
        }

        return () => {
            if (sectionId) {
                dispatch(removeStyleguideSection({ id: sectionId }));
            }
        };
    }, [sectionId]);

    const toggleButtonHandler = () => {
        if (sectionId) {
            dispatch(setIsStyleguideSectionOpen({ id: sectionId, isOpen: !isOpen }));
        } else {
            setIsVisible(!isVisible);
        }
    };

    const styles = useMemo(() => {
        if (variant === "h3") {
            return mergeProps<StyleguideSectionStyles>(baseStyles, {
                container: {
                    css: css`
                        &:first-of-type {
                            border-top: ${borderSecondLevelSection};
                        }
                    `,
                },
                title: {
                    css: css`
                        border-bottom: ${borderSecondLevelSection};
                    `,
                },
                toggle: {
                    css: css`
                        padding: ${pxToRem(10, 0)};
                        font-size: ${x => x.theme.design.typography.size.largest};
                        font-weight: ${x => x.theme.design.typography.weight.semibold};
                    `,
                },
                wrapper: {
                    css: css`
                        border-bottom: ${borderSecondLevelSection};
                    `,
                },
            });
        }
        return baseStyles;
    }, [variant]);

    if (!children) {
        return null;
    }

    return (
        <StyledWrapper {...styles?.container}>
            <Typography {...styles?.title} variant={variant || styles.title?.variant}>
                <Button
                    {...styles.toggle}
                    onClick={toggleButtonHandler}
                    icon={{ src: !isOpen ? ChevronDown : ChevronUp, position: "right" }}
                    aria-expanded={isOpen}
                    aria-controls={sectionId}
                >
                    {title}
                </Button>
            </Typography>
            {isSectionOpen && (
                <StyledWrapper {...styles?.wrapper} id={sectionId}>
                    {children}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default StyleguideSection;
